/**
 * EventCard.js — Goosecross event card.
 *
 * OVERRIDES: src/components/base/EventCard.js
 * REASON: Goose events use the cover image as a full background-image cover
 *   and overlay the body with a translucent backdrop-filter blur(30px) panel,
 *   not a solid head + body stack. Restructured DOM so the variant CSS can
 *   apply the cover + blur overlay.
 *
 * INTERFACE: render(ev) → HTMLElement, identical signature to base.
 *
 * Reference: goosecross/script.js Lines 2042-2094 (createEvents).
 */

import { emit ,isChatDisabled} from "../../../core/state.js";

const CAL_ICON =
  '<svg viewBox="0 0 13 15" width="13" height="14" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.83 0.5v2.67M9.17 0.5v2.67M0.5 5.83h12M1.83 1.83h9.34A1.33 1.33 0 0 1 12.5 3.17v9.33a1.33 1.33 0 0 1-1.33 1.33H1.83A1.33 1.33 0 0 1 0.5 12.5V3.17a1.33 1.33 0 0 1 1.33-1.34z"/></svg>';

export default class EventCard {
  render(ev) {
    const card = document.createElement("div");
    card.className = "ws-card ws-event-card ws-rich-event";

    // The whole card uses the cover image as a background — the body sits on
    // top inside a blurred translucent panel. Setting via inline style keeps
    // BE-driven URLs working without a separate `<img>` element.
    if (ev.cover_image_link) {
      card.style.backgroundImage = `url("${ev.cover_image_link}")`;
    }

    const openDetail = () => {
      if(isChatDisabled()) return;
      const label = ev.message || `Tell me more about ${ev.title || "this event"}`;
      emit("ws-prompt-select", { label, value: label });
    };

    // Cover area is fully clickable → detail view, matching old .event-cover-image.
    if (ev.cover_image_link) {
      card.style.cursor = "pointer";
      card.addEventListener("click", (e) => {
        // Ignore clicks that landed on the inner CTA or learn-more button.
        if (e.target.closest("a, button")) return;
        openDetail();
      });
    }

    // Blurred translucent inner panel — backdrop-filter set via CSS scope.
    const inner = document.createElement("div");
    inner.className = "ws-rich-event-inner";

    if (ev.title) {
      const title = document.createElement("p");
      title.className = "ws-card-title";
      title.title = ev.title;
      title.textContent = ev.title;
      inner.appendChild(title);
    }

    const metaLines = [ev.date, ev.time, ev.location].filter(Boolean);
    metaLines.forEach((text) => {
      const p = document.createElement("p");
      p.className = "ws-card-meta";
      p.textContent = text;
      inner.appendChild(p);
    });

    if (ev.sub_title || ev.description) {
      const desc = document.createElement("p");
      desc.className = "ws-card-desc";
      desc.textContent = ev.sub_title || ev.description;
      inner.appendChild(desc);
    }

    const learn = document.createElement("button");
    learn.type = "button";
    learn.className = "ws-card-learnmore";
    learn.innerHTML = `<span class="ws-learnmore-icon">${CAL_ICON}</span><span>Learn more</span>`;
    learn.addEventListener("click", openDetail);
    inner.appendChild(learn);

    // Footer row — price block + CTA.
    const footer = document.createElement("div");
    footer.className = "ws-event-footer";

    const priceBlock = document.createElement("div");
    priceBlock.className = "ws-price-block";
    if (ev.price) {
      const price = document.createElement("p");
      price.className = "ws-card-price";
      price.textContent = ev.price;
      priceBlock.appendChild(price);
    }
    if (ev.price_unit) {
      const unit = document.createElement("p");
      unit.className = "ws-card-unit";
      unit.textContent = ev.price_unit;
      priceBlock.appendChild(unit);
    }
    if (priceBlock.children.length) footer.appendChild(priceBlock);

    if (ev.cta_link) {
      const cta = document.createElement("a");
      cta.className = "ws-card-cta";
      cta.href = ev.cta_link;
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
      cta.textContent = ev.cta_text || "Reserve";
      footer.appendChild(cta);
    }
    if (footer.children.length) inner.appendChild(footer);

    card.appendChild(inner);
    return card;
  }
}
